import { useEffect, useMemo, useState } from 'react'
import { useKeyboardShortcuts } from '@st/react-util/shortcuts'

export type Command = {
  id: string
  label: string
  category: string
  href?: string
}

type Props = {
  onClose: () => void
  onSelect?: (item: Command) => void
  className?: string
  commands: Command[]
}

export function CommandPaletteDialog({ onClose, onSelect, className, commands }: Props) {
  const [query, setQuery] = useState('')

  return (
    <div
      className={`mx-auto max-w-xl transform overflow-hidden rounded-xl bg-white shadow-2xl outline outline-1 outline-black/5 ${
        className ?? ''
      }`}
    >
      <CommandPalette
        items={commands}
        query={query}
        onQueryChange={setQuery}
        onSelect={(it) => {
          onSelect?.(it)
          onClose()
          setQuery('')
        }}
        onRequestClose={() => {
          onClose()
          setQuery('')
        }}
        className="grid grid-cols-1"
      />
    </div>
  )
}

function CommandPalette({
  items,
  query,
  onQueryChange,
  onSelect,
  onRequestClose,
  className
}: {
  items: Command[]
  query: string
  onQueryChange: (q: string) => void
  onSelect: (item: Command) => void
  onRequestClose?: () => void
  className?: string
}) {
  const { filteredItems, grouped, flat, activeIndex, setActiveIndex, moveDown, moveUp } =
    useCommandPalette({ items, query })

  useKeyboardShortcuts({
    ArrowDown: () => {
      if (flat.length === 0) return
      moveDown()
    },
    ArrowUp: () => {
      if (flat.length === 0) return
      moveUp()
    },
    Enter: () => {
      if (flat.length === 0) return
      const key = flat[activeIndex]
      const [category, id] = (key ?? '').split('/')
      const chosen = filteredItems.find((it) => it.category === category && it.id === id)
      if (chosen) onSelect(chosen)
    },
    Escape: () => onRequestClose?.()
  })

  return (
    <div className={className}>
      <CommandPaletteInput query={query} onQueryChange={onQueryChange} />

      {filteredItems.length > 0 ? (
        <ul className="max-h-80 scroll-pb-2 scroll-pt-2 space-y-2 overflow-y-auto pb-2">
          {Object.entries(grouped).map(([category, list]) => (
            <CommandPaletteGroup key={category} title={category}>
              {list.map((item) => {
                const idx = filteredItems.findIndex(
                  (fi) => fi.id === item.id && fi.category === item.category
                )
                const isActive = idx === activeIndex
                return (
                  <li key={item.id}>
                    <CommandPaletteItem
                      label={item.label}
                      active={isActive}
                      onHover={() => setActiveIndex(idx)}
                      onClick={() => onSelect(item)}
                    />
                  </li>
                )
              })}
            </CommandPaletteGroup>
          ))}
        </ul>
      ) : (
        <CommandPaletteEmpty />
      )}
    </div>
  )
}

function CommandPaletteInput({
  query,
  onQueryChange
}: {
  query: string
  onQueryChange: (q: string) => void
}) {
  return (
    <input
      autoFocus
      type="text"
      placeholder="Search..."
      value={query}
      onChange={(e) => onQueryChange(e.target.value)}
      className="h-12 w-full border-0 pl-3 pr-4 text-base text-gray-900 outline-none placeholder:text-gray-400 focus:border-transparent focus:outline-none focus:ring-0 sm:text-sm"
    />
  )
}

function CommandPaletteGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <li>
      <h2 className="bg-gray-100 px-4 py-2.5 text-xs font-semibold text-gray-900">{title}</h2>
      <ul className="mt-2 text-sm text-gray-800">{children}</ul>
    </li>
  )
}

function CommandPaletteItem({
  label,
  active,
  onHover,
  onClick
}: {
  label: string
  active: boolean
  onHover: () => void
  onClick: () => void
}) {
  return (
    <button
      type="button"
      data-focus={active ? '' : undefined}
      className="w-full cursor-default select-none px-4 py-2 text-left data-[focus]:bg-blue-600 data-[focus]:text-white data-[focus]:outline-none"
      onMouseMove={onHover}
      onClick={onClick}
    >
      {label}
    </button>
  )
}

function CommandPaletteEmpty() {
  return <div className="px-4 py-10 text-center text-sm text-gray-500">No results</div>
}

function useCommandPalette({ items, query }: { items: Command[]; query: string }) {
  const filteredItems = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (term === '') return items
    return items.filter((it) => it.label.toLowerCase().includes(term))
  }, [items, query])

  const grouped = useMemo(() => {
    const acc: Record<string, Command[]> = {}
    for (const it of filteredItems) {
      acc[it.category] = acc[it.category] ? [...acc[it.category], it] : [it]
    }
    return acc
  }, [filteredItems])

  const flat = useMemo(() => filteredItems.map((it) => `${it.category}/${it.id}`), [filteredItems])
  const [activeIndex, setActiveIndex] = useState(0)
  useEffect(() => {
    setActiveIndex(0)
  }, [flat.join('|')])

  const max = flat.length - 1
  const moveDown = () => setActiveIndex((i) => (i >= max ? max : i + 1))
  const moveUp = () => setActiveIndex((i) => (i <= 0 ? 0 : i - 1))

  return { filteredItems, grouped, flat, activeIndex, setActiveIndex, moveDown, moveUp }
}
